import axios from 'axios';
import {
  AreaSearchRequest,
  ExploreFirstSearchRequest,
  ChatRequest,
  ChatResponse,
  CommuteFirstSearchRequest,
  DeepPreferenceResponse,
  GuidedStartResponse,
  ManualCriteriaRefineRequest,
  RecentSessionsResponse,
  SessionJourney,
  SmartSearchReplyLocale
} from '../types/smartSearch';
import { buildTenantApiUrl } from '../config/api';
import logger from '../utils/logger';
import secureStorage from '../utils/secureStorage';
import { getGatewayEncryptedData } from '../utils/gatewayGuestAccess';

// Use relative path to leverage proxy in package.json during development
const API_BASE_URL = '/api';

// Public GIS school-building lookup — Tenant Service.
// Full URL: <tenant base>/public/locations/school-buildings
// e.g. https://stage.allroomie.com/api/v1/tenant/public/locations/school-buildings
const SCHOOL_BUILDINGS_ENDPOINT = '/public/locations/school-buildings';

// Public unit appliance feature dictionary — Tenant Service (no auth header).
// Full URL: <tenant base>/public/unit/features/appliances
// e.g. https://stage.allroomie.com/api/v1/tenant/public/unit/features/appliances
const UNIT_FEATURE_APPLIANCES_ENDPOINT = '/public/unit/features/appliances';

// Public building amenity feature dictionary — Tenant Service (no auth header).
// Full URL: <tenant base>/public/building/features/amenities
// e.g. https://stage.allroomie.com/api/v1/tenant/public/building/features/amenities
const BUILDING_FEATURE_AMENITIES_ENDPOINT = '/public/building/features/amenities';

// One GIS school/building row from the tenant DB. Inner SchoolBuildingDetails shape
// was not fully expanded in the API docs, so fields are optional and tolerant of
// snake_case/camelCase variants the backend may emit.
export interface SchoolBuildingDetail {
  id?: number | string | null;
  schoolName?: string | null;
  school_name?: string | null;
  campusName?: string | null;
  campus_name?: string | null;
  recordType?: string | null;
  record_type?: string | null;
  buildingName?: string | null;
  building_name?: string | null;
  name?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  lat?: number | null;
  lng?: number | null;
  lon?: number | null;
  distanceMeters?: number | null;
  distance_meters?: number | null;
}

export interface SchoolBuildingLookupDetails {
  /** Whether the query resolved to a school-like place */
  schoolLike?: boolean;
  buildings?: SchoolBuildingDetail[];
  /** Whether the GIS data is currently unavailable */
  unavailable?: boolean;
}

export interface SchoolBuildingLookupResponse {
  success?: boolean;
  code?: string;
  message?: string;
  details?: SchoolBuildingLookupDetails | null;
  exceptionRequestId?: string | null;
  exceptionTimestamp?: string | null;
}

// One feature/amenity dictionary row from the Tenant Service. Mirrors the backend
// "Amenity" object shared by the unit-appliance and building-amenity endpoints.
export interface FeatureDictionaryItem {
  /** Dictionary value — corresponds to the code stored in unitInfo.amenities */
  dictValue?: string | null;
  /** Display label, e.g. "Air conditioning", "Refrigerator" */
  dictLabel?: string | null;
  /** Feature category: APPLIANCES("appliances"), LAUNDRY("laundry"), INTERIOR("interior") */
  type?: string | null;
}

export interface FeatureDictionaryResponse {
  success?: boolean;
  code?: string;
  message?: string;
  details?: FeatureDictionaryItem[] | null;
  exceptionRequestId?: string | null;
  exceptionTimestamp?: string | null;
}

// One canonical must-have amenity option from the Smart Search service. The
// option list is derived from the property search index distinct values and
// grouped into a clean, filterable set. `key` is persisted to the profile and
// sent back as must_have_in_unit / must_have_amenities; `variants` are the exact
// PSI label strings the backend matches (informational on the FE).
export interface AmenityOption {
  key: string;
  label: string;
  variants: string[];
}

export interface AmenityOptionsDetails {
  unit_features: AmenityOption[];
  building_amenities: AmenityOption[];
}

export interface AmenityOptionsResponse {
  success?: boolean;
  code?: string;
  message?: string;
  details?: AmenityOptionsDetails | null;
}

export interface AmenityConfirmationPayload {
  type?: 'amenity_confirmation';
  status?: 'ambiguous' | 'unresolved';
  resolverStatus?: string;
  rawText?: string;
  field?: 'mustHaveInUnit' | 'mustHaveAmenities';
  options?: AmenityOption[];
  showAllOptions?: boolean;
}

// All query params are optional (see API docs).
export interface SchoolBuildingLookupParams {
  /** Free-text keyword: school name, building name, or Google place name */
  query?: string;
  /** School name — preferred for exact lookups */
  schoolName?: string;
  /** Google place name */
  placeName?: string;
  /** Google formatted address */
  address?: string;
  latitude?: number | string;
  longitude?: number | string;
  /** Google place types (string or list; serialized comma-separated) */
  placeTypes?: string | string[];
  types?: string | string[];
  /** Result limit — backend default 12, max 80 */
  limit?: number;
}
const TAB_SESSION_STORAGE_KEY = 'smartSearchTabSessionId:v1';
const TAB_SESSION_EXPIRY_KEY = 'smartSearchTabSessionExpiry:v1';
const LEGACY_SHARED_SESSION_STORAGE_KEYS = [
  'smartSearchSessionId',
  'smartSearchSessionExpiry'
] as const;
const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

const clearLegacySharedSessionStorage = (): void => {
  if (typeof window === 'undefined') return;

  LEGACY_SHARED_SESSION_STORAGE_KEYS.forEach((key) => {
    // These keys previously lived in localStorage and allowed one tab to adopt
    // another tab's active conversation. Never migrate that shared session ID.
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Storage may be unavailable in restricted browser contexts.
    }

    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Remove any legacy fallback copy without blocking the current session.
    }
  });
};

// Promise cache to prevent concurrent session creation
let sessionCreationPromise: Promise<string> | null = null;

const extractSessionIdFromFunctionCalls = (functionCalls: any[]): string | undefined => {
  if (!Array.isArray(functionCalls)) return undefined;
  for (const call of functionCalls) {
    const candidate = call?.result?.session_id;
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate;
    }
  }
  return undefined;
};

export const smartSearchService = {
  async sendMessage(request: ChatRequest, isRetry: boolean = false): Promise<ChatResponse> {
    try {
      const response = await axios.post<ChatResponse>(`${API_BASE_URL}/v1/search/conversation/chat`, request);
      return response.data;
    } catch (error: any) {
      // Check if session expired on backend
      const isSessionExpired = error.response?.status === 404 ||
                               error.response?.status === 400 ||
                               error.response?.data?.error?.toLowerCase().includes('session');

      if (isSessionExpired && request.session_id && !isRetry) {
        logger.warn('Session expired on backend, clearing and retrying', { sessionId: request.session_id });

        // Clear expired session from storage
        this.clearStoredSessionId();

        // Retry without session_id (backend will create new one)
        const retryRequest = { ...request, session_id: undefined };
        return this.sendMessage(retryRequest, true);
      }

      logger.error('Error sending chat message', { error, sessionId: request.session_id });
      throw error;
    }
  },

  /**
   * Send a streaming message and receive incremental responses via callbacks
   *
   * @param request - Chat request with message and conversation history
   * @param callbacks - Callbacks for handling streaming events
   * @returns Promise that resolves when streaming completes with final response data
   */
  async sendMessageStream(
    request: ChatRequest,
    callbacks: {
      onTextDelta?: (content: string) => void;
      onFunctionCall?: (functionName: string, args: any) => void;
      onFunctionResult?: (functionName: string, result: any) => void;
      onStep?: (step: string) => void;
      onSessionId?: (sessionId: string) => void;
      onAmenityConfirmation?: (payload: AmenityConfirmationPayload) => void;
      onAuthRequired?: (reason: string) => void;
      onError?: (error: string) => void;
    }
  ): Promise<{
    message: string;
    sessionId: string | undefined;
    conversationId?: string;
    replyLocale?: SmartSearchReplyLocale;
    functionCalls: any[];
    authRequired: boolean;
    interrupted?: boolean;
  }> {
    // Create AbortController for timeout protection (60 seconds)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      logger.error('Stream timeout: Request aborted after 60 seconds');
    }, 60000);

    try {
      const url = `${API_BASE_URL}/v1/search/conversation/chat/stream`;
      logger.debug('[smartSearchService] Calling streaming endpoint:', url);

      // Get token from secureStorage (same as axios interceptor)
      const token = secureStorage.getToken();

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'X-Encrypted-Data': getGatewayEncryptedData(token),
      };

      // Add Authorization header if token exists (same logic as axios interceptor)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include', // Send cookies for authentication
        body: JSON.stringify(request),
        signal: controller.signal, // Add abort signal for timeout
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let accumulatedMessage = '';
      let sessionId: string | undefined = request.session_id;
      let conversationId = typeof request.conversation_id === 'string' && request.conversation_id.trim() !== ''
        ? request.conversation_id
        : undefined;
      let replyLocale: SmartSearchReplyLocale | undefined;
      const functionCalls: any[] = [];
      let authRequired = false;
      let buffer = '';
      let currentEvent: string | null = null;
      let currentData: string | null = null;

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          // Decode chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.substring(7).trim();
            } else if (line.startsWith('data: ')) {
              currentData = line.substring(6).trim();

              if (currentData) {
                try {
                  const data = JSON.parse(currentData);
                  const eventType = currentEvent || 'message';

                  switch (eventType) {
                    case 'text_delta':
                      if (data.content) {
                        accumulatedMessage += data.content;
                        callbacks.onTextDelta?.(data.content);
                      }
                      break;

                    case 'function_call':
                      callbacks.onFunctionCall?.(data.function, data.arguments);
                      break;

                    case 'function_result':
                      functionCalls.push({
                        function: data.function,
                        arguments: data.arguments,
                        result: data.result,
                      });
                      if (typeof data?.result?.session_id === 'string' && data.result.session_id.trim() !== '') {
                        const newId = data.result.session_id;
                        if (newId !== sessionId) {
                          logger.info('Session ID updated from function_result', { old: sessionId, new: newId });
                          sessionId = newId;
                          callbacks.onSessionId?.(newId);
                        }
                      }
                      callbacks.onFunctionResult?.(data.function, data.result);
                      break;

                    case 'status': {
                      const step = typeof data.step === 'string'
                        ? data.step
                        : typeof data.status === 'string'
                          ? data.status
                          : 'processing';
                      callbacks.onStep?.(step);
                      break;
                    }

                    case 'amenity_confirmation':
                      callbacks.onAmenityConfirmation?.(data as AmenityConfirmationPayload);
                      break;

                    case 'auth_required': {
                      authRequired = true;
                      const reason = typeof data.reason === 'string' ? data.reason : 'recommendation';
                      // Rule: R-REPLY-SIGNUP-BEFORE-RECOMMENDATIONS — the gate suppresses `done`, so the
                      // conversation the guest's draft lives in arrives here; the post-sign-in resend continues it.
                      if (typeof data.conversation_id === 'string' && data.conversation_id.trim() !== '') {
                        conversationId = data.conversation_id;
                      }
                      callbacks.onAuthRequired?.(reason);
                      break;
                    }

                    case 'done': {
                      if (data.reply_locale === 'en' || data.reply_locale === 'zh') {
                        replyLocale = data.reply_locale;
                      }
                      if (typeof data.conversation_id === 'string' && data.conversation_id.trim() !== '') {
                        conversationId = data.conversation_id;
                      }
                      let doneSessionId: string | undefined;
                      if (typeof data.session_id === 'string' && data.session_id.trim() !== '') {
                        doneSessionId = data.session_id;
                      } else if (!sessionId) {
                        doneSessionId = extractSessionIdFromFunctionCalls(data.function_calls || functionCalls);
                      }
                      if (doneSessionId && doneSessionId !== sessionId) {
                        logger.info('Session ID updated from done event', { old: sessionId, new: doneSessionId });
                        sessionId = doneSessionId;
                        callbacks.onSessionId?.(doneSessionId);
                      }
                      logger.info('Streaming completed', {
                        sessionId,
                        messageLength: accumulatedMessage.length,
                        functionCalls: functionCalls.length
                      });
                      break;
                    }

                    case 'error':
                      const errorMsg = data.message || 'Streaming error occurred';
                      logger.error('Streaming error event', { error: errorMsg });
                      callbacks.onError?.(errorMsg);
                      break;

                    case 'message':
                      // Fallback for SSE payloads that omit explicit "event:" lines.
                      if (data.content) {
                        accumulatedMessage += data.content;
                        callbacks.onTextDelta?.(data.content);
                      }
                      if (typeof data.session_id === 'string' && data.session_id.trim() !== '' && data.session_id !== sessionId) {
                        logger.info('Session ID updated from message event', { old: sessionId, new: data.session_id });
                        sessionId = data.session_id;
                        callbacks.onSessionId?.(data.session_id);
                      }
                      break;

                    default:
                      logger.warn('Unknown SSE event type', { event: eventType, data });
                  }
                } catch (e) {
                  logger.error('Failed to parse SSE data', { error: e, data: currentData });
                }

                currentEvent = null;
                currentData = null;
              }
            }
          }
        }

        if (!sessionId) {
          sessionId = extractSessionIdFromFunctionCalls(functionCalls);
        }

        return {
          message: accumulatedMessage,
          sessionId,
          conversationId,
          ...(replyLocale ? { replyLocale } : {}),
          functionCalls,
          authRequired,
        };

      } catch (error: any) {
        // Keep completed tool receipts when only the rest of the reply was lost.
        // The caller owns which receipts may be displayed; this is not a retry or a completed reply.
        if (functionCalls.length === 0) throw error;
        const errorMessage = error.name === 'AbortError'
          ? 'Request timeout: Stream took longer than 60 seconds'
          : error.message || 'Failed to stream message';
        logger.warn('Reply interrupted after tool result', { error: errorMessage, sessionId, functionCalls: functionCalls.length });
        callbacks.onError?.(errorMessage);
        return {
          message: '',
          sessionId,
          conversationId,
          functionCalls,
          authRequired,
          interrupted: true,
        };
      } finally {
        reader.releaseLock();
      }

    } catch (error: any) {
      logger.error('Error in streaming chat', { error, sessionId: request.session_id });
      // Check if error is due to abort (timeout)
      if (error.name === 'AbortError') {
        const timeoutError = 'Request timeout: Stream took longer than 60 seconds';
        callbacks.onError?.(timeoutError);
        throw new Error(timeoutError);
      }
      callbacks.onError?.(error.message || 'Failed to stream message');
      throw error;
    } finally {
      // Always clear timeout to prevent memory leaks
      clearTimeout(timeoutId);
    }
  },

  async refineManualCriteria(request: ManualCriteriaRefineRequest): Promise<DeepPreferenceResponse> {
    try {
      const response = await axios.post<DeepPreferenceResponse>(`${API_BASE_URL}/v1/search/refine`, {
        ...request,
        result_mode: request.result_mode || 'best_matches',
      }, {
        headers: {
          'X-Suppress-Global-Notification': 'true',
        },
      });
      return response.data;
    } catch (error: any) {
      logger.error('Error refining manual criteria', {
        error,
        sessionId: request.session_id,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  },

  async startCommuteFirstSearch(request: CommuteFirstSearchRequest): Promise<GuidedStartResponse> {
    try {
      const response = await axios.post<GuidedStartResponse>(`${API_BASE_URL}/v1/search/commute-first`, request, {
        headers: {
          'X-Suppress-Global-Notification': 'true',
        },
      });
      return response.data;
    } catch (error: any) {
      logger.error('Error starting commute-first search', {
        error,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  },

  async prepareSavedAreaSearch(request: AreaSearchRequest): Promise<AreaSearchRequest | null> {
    const response = await axios.post<{ ready: boolean; request?: AreaSearchRequest }>(
      `${API_BASE_URL}/v1/search/area/prepare-saved`, request,
      { headers: { 'X-Suppress-Global-Notification': 'true' } },
    );
    return response.data.ready && response.data.request ? response.data.request : null;
  },

  async startAreaSearch(request: AreaSearchRequest, replyLocale?: SmartSearchReplyLocale): Promise<GuidedStartResponse> {
    try {
      const response = await axios.post<GuidedStartResponse>(`${API_BASE_URL}/v1/search/area`, {
        ...request,
        ...(replyLocale ? { reply_locale: replyLocale } : {}),
      }, {
        headers: {
          'X-Suppress-Global-Notification': 'true',
        },
      });
      return response.data;
    } catch (error: any) {
      logger.error('Error starting area search', {
        error,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  },

  async startExploreFirstSearch(request: ExploreFirstSearchRequest): Promise<GuidedStartResponse> {
    try {
      const response = await axios.post<GuidedStartResponse>(`${API_BASE_URL}/v1/search/explore-first`, request, {
        headers: {
          'X-Suppress-Global-Notification': 'true',
        },
      });
      return response.data;
    } catch (error: any) {
      logger.error('Error starting explore-first search', {
        error,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  },

  async createSession(userId?: string): Promise<string> {
    try {
      const clientSessionId = `client-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      logger.info('Generated client-side session placeholder', { clientSessionId, userId });
      return clientSessionId;
    } catch (error) {
      logger.error('Error creating session placeholder', { error, userId });
      throw error;
    }
  },

  async getRecentSessions(userId: string, limit: number = 3): Promise<RecentSessionsResponse> {
    try {
      const response = await axios.get<RecentSessionsResponse>(`${API_BASE_URL}/v1/search/sessions/recent`, {
        params: { user_id: userId, limit }
      });
      logger.info('Fetched recent sessions', { userId, count: response.data.sessions.length });
      return response.data;
    } catch (error) {
      logger.error('Error fetching recent sessions', { error, userId });
      throw error;
    }
  },

  async getSessionById(userId: string, sessionId: string): Promise<RecentSessionsResponse['sessions'][0] | null> {
    try {
      const response = await axios.get<RecentSessionsResponse>(`${API_BASE_URL}/v1/search/sessions/recent`, {
        params: { user_id: userId, limit: 10 }
      });
      const session = response.data.sessions.find(s => s.session_id === sessionId);
      if (session) {
        logger.info('Fetched session by ID', { userId, sessionId });
      } else {
        logger.warn('Session not found in recent sessions', { userId, sessionId });
      }
      return session || null;
    } catch (error) {
      logger.error('Error fetching session by ID', { error, userId, sessionId });
      throw error;
    }
  },

  async getSessionJourney(sessionId: string): Promise<SessionJourney> {
    try {
      const response = await axios.get<SessionJourney>(`${API_BASE_URL}/v1/search/events/journey/${sessionId}`);
      logger.info('Fetched session journey', { sessionId, eventCount: response.data.events.length });
      return response.data;
    } catch (error) {
      logger.error('Error fetching session journey', { error, sessionId });
      throw error;
    }
  },

  /**
   * Fetch GIS school/building options for the Smart Search chat dropdown.
   * Public Tenant Service endpoint — no auth header required.
   */
  async getSchoolBuildings(params: SchoolBuildingLookupParams = {}): Promise<SchoolBuildingLookupResponse> {
    try {
      const queryParams: Record<string, string> = {};
      if (params.query) queryParams.query = params.query;
      if (params.schoolName) queryParams.school_name = params.schoolName;
      if (params.placeName) queryParams.place_name = params.placeName;
      if (params.address) queryParams.address = params.address;
      if (params.latitude !== undefined && params.latitude !== null && params.latitude !== '') {
        queryParams.latitude = String(params.latitude);
      }
      if (params.longitude !== undefined && params.longitude !== null && params.longitude !== '') {
        queryParams.longitude = String(params.longitude);
      }
      if (params.placeTypes) {
        queryParams.place_types = Array.isArray(params.placeTypes) ? params.placeTypes.join(',') : params.placeTypes;
      }
      if (params.types) {
        queryParams.types = Array.isArray(params.types) ? params.types.join(',') : params.types;
      }
      if (params.limit !== undefined && params.limit !== null) {
        queryParams.limit = String(params.limit);
      }

      const response = await axios.get<SchoolBuildingLookupResponse>(
        buildTenantApiUrl(SCHOOL_BUILDINGS_ENDPOINT),
        { params: queryParams }
      );
      return response.data;
    } catch (error: any) {
      logger.error('Error fetching school buildings', {
        error,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  },

  /**
   * Fetch the unit appliance feature dictionary for the amenity picker.
   * Public Tenant Service endpoint — no auth header required.
   */
  async getUnitFeatureAppliances(): Promise<FeatureDictionaryResponse> {
    try {
      const response = await axios.get<FeatureDictionaryResponse>(
        buildTenantApiUrl(UNIT_FEATURE_APPLIANCES_ENDPOINT)
      );
      return response.data;
    } catch (error: any) {
      logger.error('Error fetching unit feature appliances', {
        error,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  },

  /**
   * Fetch the building amenity feature dictionary for the amenity picker.
   * Public Tenant Service endpoint — no auth header required.
   */
  async getBuildingFeatureAmenities(): Promise<FeatureDictionaryResponse> {
    try {
      const response = await axios.get<FeatureDictionaryResponse>(
        buildTenantApiUrl(BUILDING_FEATURE_AMENITIES_ENDPOINT)
      );
      return response.data;
    } catch (error: any) {
      logger.error('Error fetching building feature amenities', {
        error,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  },

  /**
   * Fetch the canonical must-have amenity catalog from the Smart Search service.
   * Options are PSI-sourced (only things that actually exist in the index),
   * grouped into clean labels. Selected `key`s are sent back as
   * must_have_in_unit / must_have_amenities to hard-filter the search.
   */
  async getAmenityOptions(): Promise<AmenityOptionsResponse> {
    try {
      const response = await axios.get<AmenityOptionsResponse>(
        `${API_BASE_URL}/v1/search/amenity-options`
      );
      return response.data;
    } catch (error: any) {
      logger.error('Error fetching amenity options', {
        error,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  },

  async checkHealth(): Promise<boolean> {
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/search/health`);
      return response.data.status === 'healthy' || response.data.status === 'ok';
    } catch (error) {
      logger.error('Smart search service health check failed', { error });
      return false;
    }
  },

  // Per-tab active session persistence helpers.
  // NOTE: Backend does not have a DELETE endpoint for sessions.
  // Sessions auto-expire after 24 hours via MySQL scheduled event.
  // Explicit history resume writes its chosen session into this tab only.

  storeSessionId(sessionId: string): void {
    if (typeof window === 'undefined') return;

    try {
      const expiryTime = Date.now() + SESSION_DURATION_MS;
      clearLegacySharedSessionStorage();
      window.sessionStorage.setItem(TAB_SESSION_STORAGE_KEY, sessionId);
      window.sessionStorage.setItem(TAB_SESSION_EXPIRY_KEY, expiryTime.toString());
    } catch (error) {
      logger.error('Failed to store session ID', { error, sessionId });
    }
  },

  getStoredSessionId(): string | null {
    if (typeof window === 'undefined') return null;

    try {
      clearLegacySharedSessionStorage();
      const sessionId = window.sessionStorage.getItem(TAB_SESSION_STORAGE_KEY);
      const expiryTime = window.sessionStorage.getItem(TAB_SESSION_EXPIRY_KEY);

      if (!sessionId || !expiryTime) {
        return null;
      }

      // Check if session has expired
      const parsedExpiryTime = Number.parseInt(expiryTime, 10);
      if (!Number.isFinite(parsedExpiryTime) || Date.now() > parsedExpiryTime) {
        this.clearStoredSessionId();
        return null;
      }

      return sessionId;
    } catch (error) {
      logger.error('Failed to get stored session ID', { error });
      return null;
    }
  },

  clearStoredSessionId(): void {
    if (typeof window === 'undefined') return;

    try {
      clearLegacySharedSessionStorage();
      window.sessionStorage.removeItem(TAB_SESSION_STORAGE_KEY);
      window.sessionStorage.removeItem(TAB_SESSION_EXPIRY_KEY);
    } catch (error) {
      logger.error('Failed to clear stored session ID', { error });
    }
  },

  cleanupSession(): void {
    // Clear client-side session storage on logout
    // Backend session will auto-expire after 24 hours
    // Event logs and analytics data are preserved for reporting
    this.clearStoredSessionId();
    sessionCreationPromise = null;
  },

  async getOrCreateSession(userId?: string): Promise<string> {
    // Check if session already exists in storage
    const existingSession = this.getStoredSessionId();
    if (existingSession) {
      return existingSession;
    }

    // If session creation is already in progress, return the existing promise
    if (sessionCreationPromise) {
      logger.debug('Session creation already in progress, waiting for existing promise');
      return sessionCreationPromise;
    }

    // Create new session with promise caching to prevent race conditions
    sessionCreationPromise = this.createSession(userId)
      .then((sessionId) => {
        this.storeSessionId(sessionId);
        logger.info('Session created and stored', { sessionId });
        return sessionId;
      })
      .catch((error) => {
        logger.error('Failed to create session', { error, userId });
        throw error;
      })
      .finally(() => {
        // Clear the promise cache after completion
        sessionCreationPromise = null;
      });

    return sessionCreationPromise;
  },
};

export default smartSearchService;
